import React, { useEffect, useRef } from 'react';
import { FormikHelpers } from 'formik';
import { Card, Grid, LinearProgress } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { RateReview as RateReviewIcon } from '@material-ui/icons';

import { formatDate } from '../../../common/formatter';
import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineSelect from '../../../common/inline/InlineSelect';
import { WorkflowEntity } from '../../workflow/Workflow.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateSupplier } from '../Supplier.hooks';
import {
  BaseSupplierCardProps,
  Supplier,
  SupplierReviewDateOptions
} from '../Supplier.interface';

const SupplierReviewDateCard: React.FC<BaseSupplierCardProps> = (
  props: BaseSupplierCardProps
) => {
  const {
    supplier,
    supplierVersion,
    token,
    onChange,
    onUpdate,
    onError
  } = props;
  const formikRef = useRef<FormikHelpers<Partial<Supplier>> | undefined>();
  const {
    updatedSupplier,
    isUpdating,
    updateError,
    updateSupplier
  } = useUpdateSupplier(supplier.id, token);
  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.Supplier>(
    supplier.task,
    { token }
  );
  const disabled = !!supplierVersion;
  const initialData = supplierVersion || supplier;

  useEffect(() => {
    if (updatedSupplier) {
      onChange?.(updatedSupplier);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedSupplier]);

  useEffect(() => {
    if (updateError) {
      onError?.(updateError);
      // reset field back to initial value
      formikRef.current?.setFieldValue(
        'nextReviewDate',
        supplier?.nextReviewDate || null,
        true
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateError]);

  return (
    <Card>
      {isUpdating && <LinearProgress />}
      <CardTitle>
        <RateReviewIcon />
        Periodic Review
      </CardTitle>
      <CardContent>
        <InlineFormik
          strict={false}
          enableReinitialize
          isSubmitting={isUpdating}
          innerRef={(formik: any) => (formikRef.current = formik)}
          initialValues={{
            nextReviewDate: initialData.nextReviewDate || ('' as any)
          }}
          onSubmit={(values: Partial<Supplier>) => {
            values = {
              ...values,
              nextReviewDate: values.nextReviewDate || null
            };
            if (values.nextReviewDate === null) {
              values.reviewDate = null;
            }
            updateSupplier(values);
          }}
        >
          <InlineForm autoComplete='off'>
            {supplier.reviewDate && (
              <Grid item xs={12}>
                <Alert severity='info'>
                  <AlertTitle>Next Review Date</AlertTitle>
                  {formatDate(supplier.reviewDate)}
                </Alert>
              </Grid>
            )}
            <InlineSelect
              name='nextReviewDate'
              label={
                supplier.reviewDate
                  ? 'Set Next Review Date'
                  : 'Next Review Date'
              }
              options={SupplierReviewDateOptions}
              disabled={disabled || isFieldDisabled('nextReviewDate')}
            />
          </InlineForm>
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default SupplierReviewDateCard;
